import { LocalStorage } from '@pesto/hubble-shared/utils/core/BrowserStorage';

import {
  CONSENT_SETTING_COOKIE_KEY,
  FBCLID_LS_KEY,
  FLEX_REF_ID_LS_KEY,
  GCLID_LS_KEY,
  LG_LEAD_ID_LS_KEY,
  LG_SUB_ID_LS_KEY,
  MD_AFFILIATE_ID_LS_KEY,
  MSCLKID_LS_KEY,
  NERD_WALLET_REF_LS_KEY,
  PESTOANONID_LS_KEY,
  REF_LS_KEY,
  REFERRAL_CODE_LS_KEY,
  TM_SUB_ID_1_KEY,
  TM_SUB_ID_2_KEY,
  TM_SUB_ID_3_KEY,
  TRANS_ID_LS_KEY,
  UTM_CAMPAIGN_LS_KEY,
  UTM_CONTENT_LS_KEY,
  UTM_MEDIUM_LS_KEY,
  UTM_SOURCE_LS_KEY,
  UTM_STRING_LS_KEY,
} from '../../constants/local-storage-keys';
import { ADMIN_DEBUGGER_LS_KEY } from '../../constants/shared-constants';
import { getUserConsent } from '../consentHelper';
import { getCookies } from '../cookies';

export const REFERRAL_CODE_URL_PARAM = 'referralCode';

export interface UtmParams {
  utmSource: string | null;
  utmString: string | null;
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  ref: string | null;
  pestoAnonId: string | null;
  referralCode: string | null;
  nerdwalletRef: string | null;
  lgSubId?: string | null;
  lgLeadId?: string | null;
  tmSubid1?: string | null;
  tmSubid2?: string | null;
  tmSubid3?: string | null;
  mdAffiliateId?: string | null;
  transId?: string | null;
  flexRefId?: string | null;
}

export type NonNullUtmParams = {
  [K in keyof UtmParams]: NonNullable<UtmParams[K]>;
};
export const parseURL = (url: string) => {
  return new URL(url);
};
export const getUtm = (): UtmParams => {
  if (typeof window === 'undefined') {
    return {
      utmSource: null,
      utmString: null,
      gclid: null,
      fbclid: null,
      msclkid: null,
      utmMedium: null,
      utmCampaign: null,
      utmContent: null,
      ref: null,
      pestoAnonId: null,
      referralCode: null,
      nerdwalletRef: null,
    };
  }

  const urlParams = parseURL(window.location.href).searchParams;

  // whole query string
  let utmString: string | null = JSON.stringify(Object.fromEntries(urlParams.entries()));

  // utm params
  let utmSource = urlParams.get('utm_source') || null;
  let utmMedium = urlParams.get('utm_medium');
  let utmCampaign = urlParams.get('utm_campaign');
  let utmContent = urlParams.get('utm_content');

  // other params
  let gclid = urlParams.get('gclid');
  let fbclid = urlParams.get('fbclid');
  let msclkid = urlParams.get('msclkid');

  // referral
  let referralCode = urlParams.get(REFERRAL_CODE_URL_PARAM);

  // affiliate params
  let ref = urlParams.get('ref');
  let pestoAnonId = urlParams.get('pestoAnonId');
  let nerdwalletRef = urlParams.get('subid');

  // lead group
  let lgSubId = urlParams.get('lgSubId');
  let lgLeadId = urlParams.get('lgLeadId');

  let tmSubid1 = urlParams.get('tm_subid1');
  let tmSubid2 = urlParams.get('tm_subid2');
  let tmSubid3 = urlParams.get('tm_subid3');

  let mdAffiliateId = urlParams.get('md_affiliate_id');
  let transId = urlParams.get('trans_id');

  let flexRefId = urlParams.get('refid');

  // admin debugger with a secret key
  const adminDebugger = urlParams.get('ISolemnlySwearThatIAmUpToNoGood');

  if (adminDebugger) {
    LocalStorage.setItem(ADMIN_DEBUGGER_LS_KEY, adminDebugger);
    window.history.replaceState({}, document.title, window.location.pathname);
    window.location.reload();
  }
  if (utmSource && utmString) {
    LocalStorage.setItem(UTM_STRING_LS_KEY, utmString);
    LocalStorage.setItem(UTM_SOURCE_LS_KEY, utmSource);
  }

  if (utmMedium) {
    LocalStorage.setItem(UTM_MEDIUM_LS_KEY, utmMedium);
  }

  if (utmCampaign) {
    LocalStorage.setItem(UTM_CAMPAIGN_LS_KEY, utmCampaign);
  }

  if (utmContent) {
    LocalStorage.setItem(UTM_CONTENT_LS_KEY, utmContent);
  }

  if (gclid) {
    LocalStorage.setItem(GCLID_LS_KEY, gclid);
  }

  if (fbclid) {
    LocalStorage.setItem(FBCLID_LS_KEY, fbclid);
  }

  if (msclkid) {
    LocalStorage.setItem(MSCLKID_LS_KEY, msclkid);
  }

  if (ref) {
    LocalStorage.setItem(REF_LS_KEY, ref);
  }

  if (pestoAnonId) {
    LocalStorage.setItem(PESTOANONID_LS_KEY, pestoAnonId);
  }

  if (referralCode) {
    LocalStorage.setItem(REFERRAL_CODE_LS_KEY, referralCode);
  }

  if (nerdwalletRef) {
    LocalStorage.setItem(NERD_WALLET_REF_LS_KEY, nerdwalletRef);
  }

  if (lgSubId) {
    LocalStorage.setItem(LG_SUB_ID_LS_KEY, lgSubId);
  }

  if (lgLeadId) {
    LocalStorage.setItem(LG_LEAD_ID_LS_KEY, lgLeadId);
  }

  if (tmSubid1) {
    LocalStorage.setItem(TM_SUB_ID_1_KEY, tmSubid1);
  }

  if (tmSubid2) {
    LocalStorage.setItem(TM_SUB_ID_2_KEY, tmSubid2);
  }

  if (tmSubid3) {
    LocalStorage.setItem(TM_SUB_ID_3_KEY, tmSubid3);
  }

  if (mdAffiliateId) {
    LocalStorage.setItem(MD_AFFILIATE_ID_LS_KEY, mdAffiliateId);
  }
  if (transId) {
    LocalStorage.setItem(TRANS_ID_LS_KEY, transId);
  }

  if (flexRefId) {
    LocalStorage.setItem(FLEX_REF_ID_LS_KEY, flexRefId);
  }

  utmString = LocalStorage.getItem(UTM_STRING_LS_KEY);
  utmSource = LocalStorage.getItem(UTM_SOURCE_LS_KEY);
  utmMedium = LocalStorage.getItem(UTM_MEDIUM_LS_KEY);
  utmCampaign = LocalStorage.getItem(UTM_CAMPAIGN_LS_KEY);
  utmContent = LocalStorage.getItem(UTM_CONTENT_LS_KEY);
  gclid = LocalStorage.getItem(GCLID_LS_KEY);
  fbclid = LocalStorage.getItem(FBCLID_LS_KEY);
  msclkid = LocalStorage.getItem(MSCLKID_LS_KEY);
  ref = LocalStorage.getItem(REF_LS_KEY);
  pestoAnonId = LocalStorage.getItem(PESTOANONID_LS_KEY);
  referralCode = LocalStorage.getItem(REFERRAL_CODE_LS_KEY);
  nerdwalletRef = LocalStorage.getItem(NERD_WALLET_REF_LS_KEY);
  lgLeadId = LocalStorage.getItem(LG_LEAD_ID_LS_KEY);
  lgSubId = LocalStorage.getItem(LG_SUB_ID_LS_KEY);
  tmSubid1 = LocalStorage.getItem(TM_SUB_ID_1_KEY);
  tmSubid2 = LocalStorage.getItem(TM_SUB_ID_2_KEY);
  tmSubid3 = LocalStorage.getItem(TM_SUB_ID_3_KEY);
  mdAffiliateId = LocalStorage.getItem(MD_AFFILIATE_ID_LS_KEY);
  transId = LocalStorage.getItem(TRANS_ID_LS_KEY);
  flexRefId = LocalStorage.getItem(FLEX_REF_ID_LS_KEY);

  return {
    utmSource,
    utmString,
    gclid,
    fbclid,
    msclkid,
    utmMedium,
    utmCampaign,
    utmContent,
    ref,
    pestoAnonId,
    referralCode,
    nerdwalletRef,
    lgLeadId,
    lgSubId,
    tmSubid1,
    tmSubid2,
    tmSubid3,
    mdAffiliateId,
    transId,
    flexRefId,
  };
};

const getUtmWithConsent = (): UtmParams => {
  const { performance } = getUserConsent();
  if (getCookies(CONSENT_SETTING_COOKIE_KEY) !== null) {
    return performance ? getUtm() : ({} as UtmParams);
  }
  return getUtm();
};

export const getOldUtmParams = () =>
  Object.fromEntries(Object.entries(getUtmWithConsent()).filter(([value]) => !!value));

export const getOldNonNullUtmParamsForTrack = (): NonNullUtmParams => {
  const nonNullUtmParams = Object.entries(getOldUtmParams()).reduce(
    (acc, [key, value]) => {
      if (value) {
        acc.push([key as keyof UtmParams, value]);
      }
      return acc;
    },
    [] as [keyof UtmParams, NonNullable<UtmParams[keyof UtmParams]>][],
  );
  return Object.fromEntries(nonNullUtmParams) as NonNullUtmParams;
};

export const removeOldUtmParams = () => {
  const utmParams = [
    UTM_STRING_LS_KEY,
    UTM_SOURCE_LS_KEY,
    UTM_MEDIUM_LS_KEY,
    UTM_CAMPAIGN_LS_KEY,
    UTM_CONTENT_LS_KEY,
    GCLID_LS_KEY,
    FBCLID_LS_KEY,
    MSCLKID_LS_KEY,
    REF_LS_KEY,
    PESTOANONID_LS_KEY,
    REFERRAL_CODE_LS_KEY,
    NERD_WALLET_REF_LS_KEY,
    LG_LEAD_ID_LS_KEY,
    LG_SUB_ID_LS_KEY,
    TM_SUB_ID_1_KEY,
    TM_SUB_ID_2_KEY,
    TM_SUB_ID_3_KEY,
    MD_AFFILIATE_ID_LS_KEY,
    TRANS_ID_LS_KEY,
    FLEX_REF_ID_LS_KEY,
  ];

  utmParams.forEach(param => LocalStorage.removeItem(param));
};
